import { useContext, useEffect } from 'react';
import { string, object, bool } from 'prop-types';
import { AppContext } from 'src/context';
import {
  platformLogo,
  supportPhone,
  supportEmail,
} from 'src/utils/capabilities';
import { EmailLink } from 'src/components/common/EmailLink';
import { PhoneHelper } from 'src/utils/phoneHelper';
import { StyledLink } from 'src/components/common/styledComponents/styledLink';
import { getBaseUrl } from 'src/utils/getConfig';
import {
  FORBIDDEN_PAGE_URL,
  UNAUTHORIZED_PAGE_URL,
  DEPRECATED_FEATURE_PAGE,
  QUICK_LINKS_PAGE_URL,
} from 'src/constants/urlConstants';
import callUs from 'src/assets/image/call-us.png';
import { translateErrorKey, errorTexts } from './errorCopies';
import { ssoRedirect } from 'src/utils/ssoRedirect';
import {
  ErrorPage as StyledErrorPage,
  ErrorPageContainer,
  ErrorPageBody,
  ErrorPageNav,
  ErrorPageLogoImage,
  ErrorPageImageContainer,
  ErrorPageImage,
  ErrorPageTitle,
  ErrorPageContent,
  ErrorPageTextContainer,
} from './styles';
import { checkUserAccess } from 'src/utils/checkUserAccess';

const PhoneLink = phone => PhoneHelper.getPhoneLink(phone);

const ErrorPage = ({ error, location: { pathname }, isSideNavInfoShown }) => {
  const { store } = useContext(AppContext);
  const baseUrl = getBaseUrl();
  const homePageUrl = `${baseUrl}${QUICK_LINKS_PAGE_URL}`;
  const is401 = pathname === UNAUTHORIZED_PAGE_URL;
  const isForbidden = pathname === FORBIDDEN_PAGE_URL;
  const is401orForbidden = is401 || isForbidden;
  const is410 = pathname === DEPRECATED_FEATURE_PAGE;

  const showLoginLink = !store?.isLoading && !store?.originalAgent;
  const showSideNavInfo = is401orForbidden || isSideNavInfoShown;

  const errorProps = {
    t: window.translation.t,
    error,
    pathname,
  };
  const {
    title,
    subtitle,
    subtitle1_text1,
    subtitle1_text2,
    subtitle1_text3,
    subtitle2_text1,
    subtitle2_text2,
    subtitle2_text3,
    haveAnAccount,
    loginHere,
  } = errorTexts(errorProps);

  useEffect(() => {
    if (store?.originalAgent?.email) {
      checkUserAccess(store?.originalAgent?.email)
    }
  }, [])

  return (
    <StyledErrorPage>
      {showSideNavInfo && (
        <ErrorPageNav>
          <StyledLink href={homePageUrl}>
            <ErrorPageLogoImage src={platformLogo.url} alt={platformLogo.alt} />
          </StyledLink>
        </ErrorPageNav>
      )}
      <ErrorPageContainer data-test="errorPage">
        <ErrorPageBody>
          <ErrorPageImageContainer>
            <ErrorPageImage
              src={is410 ? platformLogo.c4aUrl : callUs}
              alt={translateErrorKey('defaultError.altImgText')}
            />
          </ErrorPageImageContainer>
          <ErrorPageTitle className="wow animated fadeInUp">
            {title}
          </ErrorPageTitle>
          {subtitle && (
            <ErrorPageContent className="wow animated fadeInUp">
              {subtitle}
            </ErrorPageContent>
          )}
          {!isForbidden && !is410 && (
            <ErrorPageTextContainer className="wow animated fadeInUp">
              {supportPhone && supportEmail && (
                <ErrorPageContent>
                  {subtitle1_text1} {<PhoneLink phone={supportPhone} />}{' '}
                  {subtitle1_text2} <br />
                  {subtitle1_text3}{' '}
                  {!error ? <EmailLink email={supportEmail} /> : null}
                </ErrorPageContent>
              )}
              <ErrorPageContent>
                {subtitle2_text1}{' '}
                {!is401 && (
                  <StyledLink href={homePageUrl}>{subtitle2_text2}</StyledLink>
                )}{' '}
                {is401 && (
                  <StyledLink onClick={ssoRedirect.logout}>
                    {subtitle2_text2}
                  </StyledLink>
                )}{' '}
                {subtitle2_text3}
              </ErrorPageContent>
            </ErrorPageTextContainer>
          )}
          {showLoginLink && (
            <div className="wow animated fadeInUp">
              <ErrorPageContent>
                {haveAnAccount}{' '}
                <StyledLink onClick={ssoRedirect.logout}>
                  {loginHere}
                </StyledLink>
              </ErrorPageContent>
            </div>
          )}
        </ErrorPageBody>
      </ErrorPageContainer>
    </StyledErrorPage>
  );
};

ErrorPage.propTypes = {
  error: string,
  location: object,
  isSideNavInfoShown: bool,
};

ErrorPage.defaultProps = {
  location: {},
};

export { ErrorPage };
